import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import axios from "axios";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactHtmlParser from "react-html-parser";

import { API_BASE_URL } from "../../config.js";
import Pagination from "../Pagination/Paginations.js";
import EntriesSelector from "../Pagination/EntriesSelector.js";
import useSortData from "../Pagination/SortData.js";
import DownloadButtons from "../DownloadButtons/DownloadButtons.js";

// hr_status values in xin_mobile_onduty: 1 = Pending, 2 = Approved, 3 = Rejected
const HR_APPROVED = 2;

const selectStyles = {
  control: (base) => ({
    ...base,
    minHeight: "28px",
    height: "28px",
    borderRadius: "4px",
    borderColor: "#ced4da",
    boxShadow: "none",
    fontSize: "12px",
    padding: "0 4px",
    "&:hover": { borderColor: "#b3b3b3" },
  }),
  valueContainer: (base) => ({ ...base, height: "28px", padding: "0 6px" }),
  input: (base) => ({ ...base, margin: "0px", padding: "0px" }),
  indicatorsContainer: (base) => ({ ...base, height: "28px" }),
  menu: (base) => ({ ...base, borderRadius: "4px", zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    fontSize: "12px",
    padding: "6px 10px",
    backgroundColor: state.isFocused ? "#f8f9fa" : "white",
    color: "#333",
  }),
};

const columns = [
  { label: "Emp Id", key: "employee_id" },
  { label: "Employee Name", key: "employee_name" },
  { label: "Department", key: "department_name" },
  { label: "Date", key: "date" },
  { label: "Punch In", key: "punch_in" },
  { label: "Address", key: "address" },
  { label: "Reason", key: "reason" },
  { label: "Status", key: "hr_status" },
];

const searchableFields = [
  "employee_id",
  "employee_name",
  "department_name",
  "date",
  "punch_in",
  "address",
  "reason",
];

const OndutyReport = ({ fixNavbar }) => {
  const today = new Date().toLocaleDateString("en-CA");

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [department, setDepartment] = useState("0");
  const [employee, setEmployee] = useState("ALL");

  const [departments, setDepartments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const { sortConfig, sortData, handleSort } = useSortData();

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` },
  };

  useEffect(() => {
    axios
      .get(API_BASE_URL + "/showdepartment", authHeader)
      .then((res) => setDepartments(res.data))
      .catch((err) => console.log(err));
  }, []);

  const departmentOptions = [
    { value: "0", label: "All Departments" },
    ...departments.map((d) => ({
      value: String(d.department_id),
      label: d.department_name,
    })),
  ];

  const employeeOptions = [
    { value: "ALL", label: "All Employees" },
    ...employees.map((e) => ({
      value: String(e.user_id),
      label: `${e.first_name} ${e.last_name} (${e.employee_id})`,
    })),
  ];

  const handleDepartmentChange = async (selected) => {
    const value = selected ? selected.value : "0";
    setDepartment(value);
    setEmployee("ALL");
    setEmployees([]);
    if (value === "0") return;

    try {
      const res = await axios.get(
        API_BASE_URL + "/showdeptbase_hr_emp/" + value,
        authHeader
      );
      setEmployees(res.data);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(
        API_BASE_URL + "/ondutyApprovalList",
        {
          start_date: startDate,
          end_date: endDate,
          dept_id: department,
          user_id: employee,
        },
        authHeader
      );
      setRecords(
        (res.data.ondutyapproval || []).filter(
          (r) => Number(r.hr_status) === HR_APPROVED
        )
      );
      setCurrentPage(1);
    } catch (error) {
      setRecords([]);
      const res = error.response;
      if (!res) {
        toast.error("Network error");
      } else if (res.status === 422) {
        const errs = res.data?.errors || {};
        toast.error(Object.values(errs)[0]?.[0] || "Validation failed", {
          position: "bottom-right",
          theme: "colored",
        });
      } else {
        toast.error(res.data?.message || `Error ${res.status}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const safe = (val) => String(val ?? "").toLowerCase();

  const filteredItems =
    searchTerm.trim() === ""
      ? records
      : records.filter((item) =>
          searchableFields.some((field) =>
            safe(item[field]).includes(searchTerm.toLowerCase())
          )
        );

  const sortedItems = sortData([...filteredItems]);
  const totalPages = Math.ceil(filteredItems.length / entriesPerPage);
  const indexOfLast = currentPage * entriesPerPage;
  const pageData = sortedItems.slice(indexOfLast - entriesPerPage, indexOfLast);

  const exportData = sortedItems.map((r) => ({
    "Emp Id": r.employee_id,
    "Employee Name": r.employee_name,
    Department: r.department_name,
    Date: r.date,
    // API sends "HH-mm"; spreadsheets parse that as a date, so export as "HH:mm"
    "Punch In": String(r.punch_in ?? "").replace("-", ":"),
    Address: r.address,
    Reason: r.reason,
    Status: "Approved",
  }));

  return (
    <>
      <ToastContainer />
      <div className={`section-body ${fixNavbar ? "marginTop" : ""} mt-3`}>
        <div className="container-fluid">
          <div className="card">
            <div className="card-header">
              <h3
                className="card-title mb-0"
                style={{ color: "#1d3a8a", fontWeight: 600 }}
              >
                Onduty Report
              </h3>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                <div className="row g-3 align-items-end">
                  <div className="col-md col-sm-6">
                    <div className="form-group">
                      <label className="form-label">From Date</label>
                      <DatePicker
                        selected={startDate ? new Date(startDate) : null}
                        onChange={(date) =>
                          setStartDate(date ? date.toLocaleDateString("en-CA") : "")
                        }
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        wrapperClassName="w-100"
                        maxDate={endDate ? new Date(endDate) : null}
                      />
                    </div>
                  </div>

                  <div className="col-md col-sm-6">
                    <div className="form-group">
                      <label className="form-label">To Date</label>
                      <DatePicker
                        selected={endDate ? new Date(endDate) : null}
                        onChange={(date) =>
                          setEndDate(date ? date.toLocaleDateString("en-CA") : "")
                        }
                        dateFormat="yyyy-MM-dd"
                        className="form-control"
                        wrapperClassName="w-100"
                        minDate={startDate ? new Date(startDate) : null}
                      />
                    </div>
                  </div>

                  <div className="col-md col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Department</label>
                      <Select
                        options={departmentOptions}
                        value={
                          departmentOptions.find((o) => o.value === department) ||
                          departmentOptions[0]
                        }
                        onChange={handleDepartmentChange}
                        styles={selectStyles}
                        classNamePrefix="react-select"
                      />
                    </div>
                  </div>

                  <div className="col-md col-sm-6">
                    <div className="form-group">
                      <label className="form-label">Employee</label>
                      <Select
                        options={employeeOptions}
                        value={
                          employeeOptions.find((o) => o.value === employee) ||
                          employeeOptions[0]
                        }
                        onChange={(s) => setEmployee(s ? s.value : "ALL")}
                        isDisabled={employees.length === 0}
                        styles={selectStyles}
                        classNamePrefix="react-select"
                      />
                    </div>
                  </div>

                  <div className="col-md-auto col-sm-6">
                    <div className="form-group">
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        GET
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
              <div className="d-flex align-items-center" style={{ gap: "8px" }}>
                <DownloadButtons data={exportData} filename="Onduty_Report" />
                {records.length > 0 && (
                  <span style={{ fontSize: "13px" }}>
                    Total Approved: <b>{records.length}</b>
                  </span>
                )}
              </div>
              <div className="d-flex align-items-center" style={{ gap: "12px" }}>
                <EntriesSelector
                  entriesPerPage={entriesPerPage}
                  setEntriesPerPage={setEntriesPerPage}
                  setCurrentPage={setCurrentPage}
                />
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1);
                  }}
                  style={{ width: "200px", height: "30px", fontSize: "13px" }}
                />
              </div>
            </div>

            <div className="card-body">
              {loading ? (
                <div className="d-flex justify-content-center">
                  <div className="spinner"></div>
                </div>
              ) : (
                <>
                  <div className="table-responsive sticky-table-wrap">
                    <table className="table table-hover table-striped table-vcenter text-nowrap mb-0 sticky-table">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          {columns.map((col) => (
                            <th
                              key={col.key}
                              onClick={() => handleSort(col.key)}
                              style={{ cursor: "pointer" }}
                            >
                              {col.label}
                              {sortConfig.key === col.key
                                ? sortConfig.direction === "asc"
                                  ? " ▲"
                                  : " ▼"
                                : " ⇅"}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {pageData.length > 0 ? (
                          pageData.map((record, i) => (
                            <tr key={record.id}>
                              <td>{indexOfLast - entriesPerPage + i + 1}</td>
                              <td>{record.employee_id}</td>
                              <td>{record.employee_name}</td>
                              <td>{record.department_name}</td>
                              <td>{record.date}</td>
                              <td>{record.punch_in}</td>
                              <td>{record.address}</td>
                              <td>{record.reason}</td>
                              <td>{ReactHtmlParser(record.hr_approve_status)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td
                              colSpan={columns.length + 1}
                              style={{ color: "red", textAlign: "center", fontWeight: "bold" }}
                            >
                              No records found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  fixNavbar: state.settings.isFixNavbar,
});

export default connect(mapStateToProps)(OndutyReport);
